import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Search, FileText, AlertCircle, MoreVertical, Pencil,
  ArrowUpRight, Send, Mail, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useInvoices, useSendInvoice, useSendReminder, useVoidInvoice } from '@/hooks/useFinancials';
import {
  INVOICE_STATUS_LABELS, INVOICE_STATUSES,
  type Invoice, type InvoiceStatus,
} from '@/types/financials';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InvoiceStatusBadge } from '@/components/financials/InvoiceStatusBadge';
import { InvoiceFormModal } from '@/components/financials/InvoiceFormModal';
import { formatCurrency } from '@/lib/utils';

export function InvoicesPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'financials', 'write') : false;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | InvoiceStatus>('');

  const { data, isLoading, error } = useInvoices();
  const sendInvoice = useSendInvoice();
  const sendReminder = useSendReminder();
  const voidInvoice = useVoidInvoice();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [voiding, setVoiding] = useState<Invoice | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((i) => {
      if (status && i.status !== status) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const hay = `${i.number} ${i.clientName ?? ''} ${i.orderNumber ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, search, status]);

  const stats = useMemo(() => {
    if (!data) return { open: 0, overdue: 0, openValue: 0, paidThisMonth: 0 };
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const openStatuses: InvoiceStatus[] = ['sent', 'partial', 'past_due'];
    return {
      open: data.filter((i) => openStatuses.includes(i.status)).length,
      overdue: data.filter((i) => i.status === 'past_due').length,
      openValue: data
        .filter((i) => openStatuses.includes(i.status))
        .reduce((s, i) => s + (i.totals?.balanceDue ?? 0), 0),
      paidThisMonth: data
        .filter((i) => i.status === 'paid' && i.updatedAt && new Date(i.updatedAt) >= monthStart)
        .reduce((s, i) => s + (i.totals?.total ?? 0), 0),
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load invoices"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function onSend(inv: Invoice) {
    try {
      await sendInvoice.mutateAsync(inv.id);
      toast.success(`Sent ${inv.number} via SES`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Send failed.');
    }
  }

  async function onRemind(inv: Invoice) {
    try {
      await sendReminder.mutateAsync(inv.id);
      toast.success(`Reminder sent for ${inv.number}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Reminder failed.');
    }
  }

  async function onConfirmVoid() {
    if (!voiding) return;
    try {
      await voidInvoice.mutateAsync({ id: voiding.id, reason: 'Voided from invoice list' });
      toast.success(`Voided ${voiding.number}`);
      setVoiding(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Void failed.');
    }
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Open"           value={String(stats.open)} />
        <Stat label="Overdue"        value={String(stats.overdue)} alert={stats.overdue > 0} />
        <Stat label="Open Value"     value={stats.openValue ? formatCurrency(stats.openValue) : '—'} accent />
        <Stat label="Paid · MTD"     value={stats.paidThisMonth ? formatCurrency(stats.paidThisMonth) : '—'} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by invoice #, client, or WO…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}
            options={[
              { value: '', label: 'All statuses' },
              ...INVOICE_STATUSES.map((s) => ({ value: s, label: INVOICE_STATUS_LABELS[s] })),
            ]}
          />
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Invoice
          </Button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={FileText}
            title={search || status ? 'No invoices match your filters' : 'No invoices yet'}
            description={search || status ? 'Try clearing some filters.' : 'Create your first invoice to start AR.'}
            action={canWrite && !search && !status && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                New Invoice
              </Button>
            )}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">Invoice</th>
                <th className="kicker px-6 py-3 font-normal">Client</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal">Issued</th>
                <th className="kicker px-6 py-3 font-normal">Due</th>
                <th className="kicker px-6 py-3 font-normal text-right">Total</th>
                <th className="kicker px-6 py-3 font-normal text-right">Balance</th>
                <th className="kicker px-6 py-3 font-normal w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const bal = inv.totals?.balanceDue ?? 0;
                const overdue = inv.status === 'past_due';
                return (
                  <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 transition-colors group">
                    <td className="px-6 py-4">
                      <Link to={`/financials/invoices/${inv.id}`} className="font-mono text-brass-300 hover:text-brass-200">
                        {inv.number}
                      </Link>
                      {inv.orderNumber && (
                        <div className="text-2xs text-text-dim font-mono mt-0.5">from {inv.orderNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-text">{inv.clientName ?? <span className="text-text-dim">—</span>}</td>
                    <td className="px-6 py-4"><InvoiceStatusBadge status={inv.status} /></td>
                    <td className="px-6 py-4 text-text-muted tabular text-xs">
                      {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </td>
                    <td className={`px-6 py-4 tabular text-xs ${overdue ? 'text-danger' : 'text-text-muted'}`}>
                      {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text">
                      {formatCurrency(inv.totals?.total ?? 0)}
                    </td>
                    <td className="px-6 py-4 text-right tabular font-medium">
                      {bal > 0 ? (
                        <span className={overdue ? 'text-danger' : 'text-text'}>{formatCurrency(bal)}</span>
                      ) : (
                        <span className="text-text-dim">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      {canWrite && (
                        <>
                          <button
                            onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === inv.id ? null : inv.id); }}
                            className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                            aria-label="Actions"
                          >
                            <MoreVertical size={15} />
                          </button>
                          {openMenu === inv.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                              <div className="absolute right-4 top-12 z-20 w-48 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                                <Link to={`/financials/invoices/${inv.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover">
                                  <ArrowUpRight size={13} /> Open
                                </Link>
                                {inv.status === 'draft' && (
                                  <button onClick={() => { onSend(inv); setOpenMenu(null); }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                    <Send size={13} /> Send via Email
                                  </button>
                                )}
                                {['sent', 'partial', 'past_due'].includes(inv.status) && (
                                  <button onClick={() => { onRemind(inv); setOpenMenu(null); }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                    <Mail size={13} /> Send Reminder
                                  </button>
                                )}
                                {inv.status !== 'void' && inv.status !== 'paid' && (
                                  <button onClick={() => { setEditing(inv); setOpenMenu(null); }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                    <Pencil size={13} /> Edit
                                  </button>
                                )}
                                {inv.status !== 'void' && (
                                  <>
                                    <div className="my-1 border-t border-border" />
                                    <button onClick={() => { setVoiding(inv); setOpenMenu(null); }}
                                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                                      <XCircle size={13} /> Void
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <InvoiceFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <InvoiceFormModal open={!!editing} onClose={() => setEditing(null)} invoice={editing} />
      <ConfirmDialog
        open={!!voiding}
        onClose={() => setVoiding(null)}
        onConfirm={onConfirmVoid}
        loading={voidInvoice.isPending}
        destructive
        title={`Void ${voiding?.number}?`}
        confirmLabel="Void invoice"
        description="A voided invoice cannot be reactivated. Its balance is removed from AR. Use for cancellations and wrongly-issued invoices."
      />
    </div>
  );
}

function Stat({ label, value, accent, alert }: { label: string; value: string; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : alert ? 'text-danger' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}
